import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Ellipsis, KeyRound, Plus, Power, RadioTower, TriangleAlert } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import {
  devicesQuery,
  settingsQuery,
  useAddDevice,
  useDisableDevice,
  useLockerState,
  useRotateDevice,
  useSaveSettings,
  type BranchSettings,
  type Device,
  type DeviceDirection,
  type DeviceKind,
  type DeviceWithKey,
} from '@/api/access'
import { ApiError } from '@/api/client'
import { branchesQuery } from '@/api/queries'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import { useNow } from '@/app/access/use-now'
import { useCompanyCaller } from '@/app/company'
import { CopyBox } from '@/app/copy-box'
import { boardText, deviceHealth, durationText, entryBranches, LOCKER_MODES, minutesBetween, parseBoard, type LockerMode } from '@/domain/access'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { SelectField } from '@/ui/select'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { NeutralSegmented } from '@/ui/segmented'
import { LoadingBlock } from '@/ui/spinner'
import { TextArea } from '@/ui/textarea'

export const Route = createFileRoute('/_app/c/$orgId/entry/settings')({
  component: EntrySettings,
})

function EntrySettings() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="settings" allowed={(can) => can.setUp}>
      {(ctx) => <SettingsScreen key={ctx.branch.id} {...ctx} />}
    </EntryShell>
  )
}

/// «Налаштування» of the door, per club: how lockers work (`settings ≥
/// edit`) and, for `settings = manage`, the devices at the door.
function SettingsScreen(ctx: EntryContext) {
  const settings = useQuery(settingsQuery(ctx.orgId, ctx.branch.id))
  return (
    <div className="flex max-w-[840px] flex-col gap-12">
      {settings.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : settings.isError ? (
        <InlineBanner kind="error" message={errorText(settings.error)} action={{ label: t.app.retry, onClick: () => void settings.refetch() }} />
      ) : (
        <LockerForm {...ctx} settings={settings.data} />
      )}
      {ctx.can.devices ? <Devices {...ctx} /> : null}
    </div>
  )
}

/// Locker mode (three ways, a line each), the key board in `keys` mode and
/// the auto-close. The server refuses leaving `keys` — or dropping a key from
/// the board — while those keys are out (L2); the numbers are shown.
function LockerForm({ orgId, branch, can, settings }: EntryContext & { settings: BranchSettings }) {
  const s = t.access.settings
  const save = useSaveSettings(orgId, branch.id)
  const locker = useLockerState(orgId, branch.id)
  const initialBoard = boardText(settings.lockers.map((l) => l.number))
  const [mode, setMode] = useState<LockerMode>(settings.lockerMode)
  const [board, setBoard] = useState(initialBoard)
  const [minutes, setMinutes] = useState(String(settings.autoCloseMinutes))
  const [saved, setSaved] = useState(false)
  const parsed = parseBoard(board)
  const minutesValue = Number(minutes)
  const minutesOk = Number.isInteger(minutesValue) && minutesValue >= 30 && minutesValue <= 1440
  const boardOk = mode !== 'keys' || parsed !== null
  const changed = mode !== settings.lockerMode || minutesValue !== settings.autoCloseMinutes || (mode === 'keys' && board.trim() !== initialBoard)
  const issued = settings.lockers.filter((l) => l.state === 'issued').length
  const lost = settings.lockers.filter((l) => l.state === 'lost')

  const refusal = (() => {
    if (!save.isError) return null
    const error = save.error
    if (error instanceof ApiError && (error.code === 'keys_out' || error.code === 'lockers_rented')) {
      const numbers = Array.isArray(error.data['numbers']) ? (error.data['numbers'] as unknown[]).map(String) : []
      // LR6 (specs/access.md §10b): rented lockers never leave the board.
      return error.code === 'keys_out' ? s.keysOut(numbers.join(', ')) : s.lockersRented(numbers.join(', '))
    }
    return errorText(error)
  })()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!minutesOk || !boardOk || !changed) return
    setSaved(false)
    save.mutate(
      {
        lockerMode: mode,
        autoCloseMinutes: minutesValue,
        ...(mode === 'keys' && board.trim() !== initialBoard ? { lockers: board.trim() } : {}),
      },
      { onSuccess: () => setSaved(true) },
    )
  }

  const readOnly = !can.setUp

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <SectionHeader title={s.lockers} inset={false} />
        <div className="flex flex-col gap-6 rounded-[16px] bg-surface p-5">
          <div className="flex flex-col gap-2">
            <span className="text-caption font-medium text-text-2">{s.mode}</span>
            <NeutralSegmented
              label={s.mode}
              onSurface
              className="w-full max-w-[460px]"
              value={mode}
              options={LOCKER_MODES.map((value) => ({ value, label: s.modes[value], disabled: readOnly }))}
              onChange={(next) => {
                setMode(next)
                setSaved(false)
              }}
            />
            <p className="text-caption text-text-2">{s.modeHints[mode]}</p>
          </div>

          {mode === 'keys' ? (
            <div className="flex flex-col gap-2">
              <TextArea
                label={s.board}
                rows={2}
                value={board}
                disabled={readOnly}
                placeholder="1-80, Ж1-Ж10"
                error={parsed === null ? s.boardInvalid : undefined}
                hint={parsed && parsed.length > 0 ? `${s.boardCount(parsed.length)}. ${s.boardHint}` : s.boardHint}
                onChange={(event) => {
                  setBoard(event.target.value)
                  setSaved(false)
                }}
              />
              {settings.lockerMode === 'keys' && settings.lockers.length > 0 ? (
                <p className="text-caption text-muted tabular">{s.boardState(settings.lockers.length, issued, lost.length)}</p>
              ) : null}
            </div>
          ) : null}

          <Field
            label={s.autoClose}
            inputMode="numeric"
            className="max-w-[260px]"
            value={minutes}
            disabled={readOnly}
            error={minutesOk ? undefined : s.autoCloseInvalid}
            hint={s.autoCloseHint}
            trailing={<span className="pr-4 text-callout text-muted">{s.autoCloseUnit}</span>}
            onChange={(event) => {
              setMinutes(event.target.value.replace(/[^\d]/g, ''))
              setSaved(false)
            }}
          />
        </div>
      </section>

      {lost.length > 0 && settings.lockerMode === 'keys' ? (
        <section className="flex flex-col gap-2">
          <SectionHeader title={s.lostKeys} count={lost.length} />
          <div className="row-list overflow-hidden rounded-[16px] bg-surface">
            {lost.map((key) => (
              <div key={key.number} className="compact-row flex min-h-12 items-center gap-3 px-4">
                <KeyRound aria-hidden="true" className="size-4 text-muted" />
                <span className="flex-1 text-callout font-medium text-text tabular">{key.number}</span>
                {key.note ? <span className="truncate text-caption text-text-2">{key.note}</span> : null}
                {can.admit ? (
                  <Button
                    compact
                    variant="secondary"
                    busy={locker.isPending && locker.variables?.number === key.number}
                    onClick={() => locker.mutate({ number: key.number, state: 'free' })}
                  >
                    {s.found}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {refusal ? <InlineBanner kind="error" message={refusal} /> : null}
      {saved ? <InlineBanner kind="done" message={s.saved} /> : null}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" busy={save.isPending} disabled={!changed || !minutesOk || !boardOk}>
          {save.isPending ? t.app.saving : t.app.save}
        </Button>
      </div>
    </form>
  )
}

const KINDS: readonly DeviceKind[] = ['qr_reader', 'card_reader', 'face_terminal', 'turnstile']
const DIRECTIONS: readonly DeviceDirection[] = ['in', 'out', 'both']

/// The readers, terminals and turnstiles of the company — `settings =
/// manage`. A key is shown ONCE (at registration or on rotation), stored
/// hashed; a device that went quiet for more than 5 minutes is flagged.
function Devices({ orgId, branch }: EntryContext) {
  const s = t.access.devices
  const { access } = useCompanyCaller(orgId)
  const devices = useQuery(devicesQuery(orgId))
  const branches = useQuery(branchesQuery(orgId))
  const open = entryBranches(access, branches.data ?? [])
  const branchName = (id: string) => branches.data?.find((b) => b.id === id)?.name ?? '—'
  const now = useNow(30_000)
  const rotate = useRotateDevice(orgId)
  const disable = useDisableDevice(orgId)
  const [adding, setAdding] = useState(false)
  const [revealed, setRevealed] = useState<DeviceWithKey | null>(null)
  const [rotating, setRotating] = useState<Device | null>(null)
  const [disabling, setDisabling] = useState<Device | null>(null)
  const multi = open.length > 1

  const seen = (device: Device) => {
    const health = deviceHealth(device, now)
    const ago = device.lastSeenAt ? durationText(minutesBetween(device.lastSeenAt, now)) : ''
    if (health === 'off') return <span className="text-muted">{s.health.off}</span>
    if (health === 'never') return <span className="text-muted">{s.health.never}</span>
    if (health === 'stale') {
      return (
        <span className="inline-flex items-center gap-1.5 text-text">
          <TriangleAlert aria-hidden="true" className="size-3.5 text-warning" strokeWidth={2.25} />
          {s.health.stale(ago)}
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="size-1.5 rounded-full bg-success" />
        {s.health.ok(ago)}
      </span>
    )
  }

  const columns: readonly DataColumn<Device>[] = [
    { key: 'name', header: s.columns.name, width: 'minmax(0,1.3fr)', cell: (d) => d.name },
    { key: 'kind', header: s.columns.kind, width: 'minmax(0,1fr)', cell: (d) => s.kinds[d.kind] },
    { key: 'direction', header: s.columns.direction, width: 'minmax(0,0.8fr)', hideBelow: 'md', cell: (d) => s.directions[d.direction] },
    ...(multi
      ? [{ key: 'branch', header: s.columns.branch, width: 'minmax(0,0.9fr)', hideBelow: 'lg', cell: (d: Device) => branchName(d.branchId) } satisfies DataColumn<Device>]
      : []),
    { key: 'seen', header: s.columns.seen, width: 'minmax(0,1.1fr)', mobile: 'trailing', cell: seen },
  ]

  const addButton = (
    <Button icon={<Plus className="size-4" strokeWidth={2.5} />} compact variant="secondary" onClick={() => setAdding(true)}>
      {s.add}
    </Button>
  )

  return (
    <section className="flex flex-col gap-3">
      <SectionHeader
        title={s.title}
        inset={false}
        aside={devices.data && devices.data.length > 0 ? addButton : undefined}
        className="items-center"
      />
      {devices.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : devices.isError ? (
        <InlineBanner kind="error" message={errorText(devices.error)} action={{ label: t.app.retry, onClick: () => void devices.refetch() }} />
      ) : devices.data.length === 0 ? (
        <div className="rounded-[16px] bg-surface">
          <EmptyState icon={<RadioTower />} title={s.empty} message={s.emptyMessage} action={addButton} />
        </div>
      ) : (
        <DataList
          label={s.title}
          columns={columns}
          rows={devices.data}
          rowKey={(d) => d.id}
          rowTone={(d) => (d.disabled ? 'muted' : 'normal')}
          rowActions={(device) => (
            <RowMenu label={`${t.app.more}: ${device.name}`} trigger={<Ellipsis className="size-[18px]" />}>
              <RowMenuItem icon={<KeyRound className="size-4" />} onSelect={() => setRotating(device)}>
                {s.rotate}
              </RowMenuItem>
              {device.disabled ? null : (
                <RowMenuItem destructive icon={<Power className="size-4" />} onSelect={() => setDisabling(device)}>
                  {s.disable}
                </RowMenuItem>
              )}
            </RowMenu>
          )}
        />
      )}
      <p className="px-4 text-caption text-muted">{s.subtitle}</p>

      {adding ? (
        <AddDevicePanel
          orgId={orgId}
          branches={open}
          defaultBranch={branch.id}
          onClose={() => setAdding(false)}
          onAdded={(answer) => {
            setAdding(false)
            setRevealed(answer)
          }}
        />
      ) : null}
      {revealed ? <KeyPanel answer={revealed} onClose={() => setRevealed(null)} /> : null}

      <ConfirmDialog
        open={rotating !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRotating(null)
            rotate.reset()
          }
        }}
        destructive={false}
        title={rotating ? s.rotateTitle(rotating.name) : ''}
        message={s.rotateMessage}
        confirmLabel={s.rotate}
        busy={rotate.isPending}
        error={rotate.isError ? <InlineBanner kind="error" message={errorText(rotate.error)} /> : null}
        onConfirm={() => {
          if (!rotating) return
          rotate.mutate(rotating.id, {
            onSuccess: (answer) => {
              setRotating(null)
              setRevealed(answer)
            },
          })
        }}
      />
      <ConfirmDialog
        open={disabling !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDisabling(null)
            disable.reset()
          }
        }}
        title={disabling ? s.disableTitle(disabling.name) : ''}
        message={s.disableMessage}
        confirmLabel={s.disable}
        busy={disable.isPending}
        error={disable.isError ? <InlineBanner kind="error" message={errorText(disable.error)} /> : null}
        onConfirm={() => {
          if (!disabling) return
          disable.mutate(disabling.id, { onSuccess: () => setDisabling(null) })
        }}
      />
    </section>
  )
}

const Select = <V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: V
  options: readonly Readonly<{ value: V; label: string }>[]
  onChange: (value: V) => void
}) => (
  <SelectField label={label} value={value} options={options} onChange={onChange} />
)

function AddDevicePanel({
  orgId,
  branches,
  defaultBranch,
  onClose,
  onAdded,
}: {
  orgId: string
  branches: readonly { id: string; name: string }[]
  defaultBranch: string
  onClose: () => void
  onAdded: (answer: DeviceWithKey) => void
}) {
  const s = t.access.devices
  const add = useAddDevice(orgId)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<DeviceKind>('turnstile')
  const [direction, setDirection] = useState<DeviceDirection>('in')
  const [branchId, setBranchId] = useState(defaultBranch)
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) {
      setNameError(s.form.nameRequired)
      return
    }
    add.mutate({ branchId, kind, direction, name: name.trim() }, { onSuccess: onAdded })
  }

  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={s.form.title}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={add.isPending}>
            {add.isPending ? s.form.submitting : s.form.submit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Field
          data-autofocus=""
          label={s.form.name}
          placeholder={s.form.namePlaceholder}
          value={name}
          error={nameError}
          onChange={(event) => {
            setName(event.target.value)
            if (event.target.value.trim()) setNameError(undefined)
          }}
        />
        <Select label={s.form.kind} value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k, label: s.kinds[k] }))} />
        <div className="flex flex-col gap-1.5">
          <span className="text-caption font-medium text-text-2">{s.form.direction}</span>
          <NeutralSegmented
            label={s.form.direction}
            onSurface
            value={direction}
            onChange={setDirection}
            options={DIRECTIONS.map((d) => ({ value: d, label: s.directions[d] }))}
          />
        </div>
        {branches.length > 1 ? (
          <Select
            label={s.form.branch}
            value={branchId}
            onChange={setBranchId}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />
        ) : null}
        {add.isError ? <InlineBanner kind="error" message={errorText(add.error)} /> : null}
      </div>
    </SidePanel>
  )
}

/// The key, once. The device sends `Authorization: Device <key>` to
/// `/access/orgs/{orgId}/devices/{id}/…` — both are shown, ready to copy.
function KeyPanel({ answer, onClose }: { answer: DeviceWithKey; onClose: () => void }) {
  const s = t.access.devices
  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={s.keyTitle}
      description={answer.device.name}
      footer={
        <Button variant="primary" onClick={onClose}>
          {s.keyDone}
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <InlineBanner kind="stale" message={s.keyWarning} />
        <CopyBox label={s.keyId} value={answer.device.id} />
        <CopyBox label={s.keyValue} value={answer.key} />
        <CopyBox label={s.keyHeader} value={`Authorization: Device ${answer.key}`} />
      </div>
    </SidePanel>
  )
}
